import { h, Component } from 'preact';
import {
  IActiveSplashScreen,
  IServiceBoardDisclaimer,
  ISplashScreen,
  ISplashScreenProps,
  ISplashScreenState
} from '../sgwt-splash-screen.types';
import { widgetConfiguration } from '@sgwt-widget/core';
import { BUS_ACCESS_TOKEN } from '../../common/bus-topics';
import { SubscriptionHandle } from '@sgwt-widget/core/lib/bus/Bus';
import SplashScreensCarousel from './SplashScreensCarousel';

const Modal = require('react-bootstrap/lib/Modal');

export default class SplashScreen extends Component<ISplashScreenProps, ISplashScreenState> {
  private sgConnectV2TokenSubscription?: SubscriptionHandle;
  private authToken?: string;
  private apiEndpoint: string;
  private apiDisclaimerEndpoint: string;
  private disclaimerEndpoint: string;
  private sgConnectV2: boolean;
  private activeScreenToDisplay: boolean;

  constructor(props: ISplashScreenProps) {
    super(props);
    this.state = this.getInitialState();

    this.setApiEndpoints();
    this.sgConnectV2 = !props.sgConnectSupport || props.sgConnectSupport.toLowerCase() === 'sg-connect-v2';
    // This flag indicates that the active screen should be displayed to the user.
    // It may happens that the authentication is not yet accomplished when the component is mounted, which
    // will make the display fails. Thus, once the authentication is done, we check the flag, which means that
    // we will try to redisplay the active screen.
    this.activeScreenToDisplay = !props.manualActivation;
  }

  private logDebug(...messages: any[]) {
    if (this.props.debug) {
      widgetConfiguration().log('[sgwt-splash-screen::debug]', ...messages);
    }
  }

  /**
   * Set the API Endpoint corresponding to the current environment.
   */
  private setApiEndpoints() {
    const endpoints = {
      api: {
        local: 'http://localhost:9999/api',
        homologation: 'https://sgwt-services-uat.fr.world.socgen/api',
        production: 'https://shared.sgmarkets.com/api'
      },
      apiDisclaimer: {
        local: 'https://sgr-web-serviceboard-uat.fr.world.socgen/api/v2/userdisclaimers',
        homologation: 'https://sgr-web-serviceboard-uat.fr.world.socgen/api/v2/userdisclaimers',
        production: 'https://www.sgmarkets.com/api/v1/disclaimers'
      },
      disclaimer: {
        local: 'https://sgr-web-serviceboard-uat.fr.world.socgen/disclaimer/page',
        homologation: 'https://sgr-web-serviceboard-uat.fr.world.socgen/disclaimer/page',
        production: 'https://www.sgmarkets.com/disclaimer/page'
      }
    };
    const envFromConfig = widgetConfiguration().environment.toLowerCase();
    if (
      envFromConfig === 'dev' || envFromConfig === 'development' ||
      envFromConfig === 'hom' || envFromConfig === 'homologation' ||
      envFromConfig === 'uat'
    ) {
      this.apiEndpoint = endpoints.api.homologation;
      this.apiDisclaimerEndpoint = endpoints.apiDisclaimer.homologation;
      this.disclaimerEndpoint = endpoints.disclaimer.homologation;
    } else if (envFromConfig === 'local') {
      this.apiEndpoint = endpoints.api.local;
      this.apiDisclaimerEndpoint = endpoints.apiDisclaimer.local;
      this.disclaimerEndpoint = endpoints.disclaimer.local;
    } else {
      this.apiEndpoint = endpoints.api.production;
      this.apiDisclaimerEndpoint = endpoints.apiDisclaimer.production;
      this.disclaimerEndpoint = endpoints.disclaimer.production;

    }
    this.logDebug(`API endpoint ${this.apiEndpoint} for environment ${envFromConfig}.`);
  }

  getInitialState(): ISplashScreenState {
    return {
      show: false,
      history: [],
      screen: null
    };
  }

  /**
   * The component has been updated. Potentially this has been called by the skateJs component (sgwt-splash-screen.tsx)
   * with the `show()` or `showHistory()` function. Therefore, we need to check if the screen has to be displayed...
   */
  public componentDidUpdate(previousProps: ISplashScreenProps) {
    let callHistory = false;
    // The component just switched to "history" mode, so we retrieve the screens.
    if (this.props.mode === 'history' && previousProps.mode !== 'history') {
      this.retrieveHistoryScreens();
      callHistory = true;
    }
    if (this.props.visible && !this.state.show) {
      this.setState({
        show: true
      });
      // Do we have to retrieve history again? For example when we closed the history, and ask to show it again...
      if (this.props.mode === 'history' && !callHistory) {
        this.retrieveHistoryScreens();
      }
      if (this.props.mode === 'active') {
        this.retrieveActiveScreen();
      }
    } else {
      this.props.onNotShown();
    }
  }

  /**
   * A SG Connect token has been found (either by `window.sgwtConnect` or the Widgets bus).
   * If this is the first token we got, we call the API to know if a new screen has to be displayed to the user...
   */
  private gotToken(token: string | undefined, origin: string) {
    const firstToken = token && !this.authToken;
    this.logDebug(`Token updated by ${origin}: ${token}. Is first one received? ${firstToken}`);
    this.authToken = token;
    // We are in SG Connect v2 mode. If we get the token for the first time, and we need to retrieve the active
    // screen (i.e. we didn't get it yet, and the widget is not in manual mode), then we call the API with the
    // given token.
    if (firstToken && this.activeScreenToDisplay) {
      this.retrieveActiveScreen();
    }
  }

  /**
  * We look for `window.sgwtConnect` to find the access token.
  */
  private tryToRetrieveTokenFromGlobalLibrary() {
    const sgwtConnect: any | undefined = (window as any).sgwtConnect;
    if (sgwtConnect) {
      const token = sgwtConnect.getAuthorizationHeader!();
      if (token) {
        this.gotToken(token, 'SGWT Connect library');
        return true;
      }
    }
    return false;
  }

  /**
   * The component will be mounted. If it is configured to use SG Connect v2, then we subscribe to the bus to be
   * aware of user connection status and authorization token.
   */
  public componentWillMount() {
    if (this.sgConnectV2) {
      // We first try to retrieve the SG Connect token from the `window.sgwtConnect` instance if it exists.
      // Otherwise we will rely on the Widgets Bus.
      if (!this.tryToRetrieveTokenFromGlobalLibrary()) {
        this.sgConnectV2TokenSubscription = widgetConfiguration().bus
          .subscribe<string>(BUS_ACCESS_TOKEN, token => {
            this.gotToken(token, 'Widgets Bus');
          });
      }
    }
  }

  /**
   * The component will be unmounted, so we have to unsubscribe to bus subscriptions for SG Connect v2.
   */
  public componentWillUnmount() {
    if (this.sgConnectV2TokenSubscription) {
      widgetConfiguration().bus.unsubscribe(this.sgConnectV2TokenSubscription);
      this.logDebug('Component unmounted, subscription to bus cancelled');
    }
  }

  /**
   * When the component is mounted, and when we are in SSO v1 compatibility, we call the API to retrieve a potential
   * active screen (if not already displayed before). On SG Connect v2 mode, this will be done once we get an
   * authorization token from the Widgets bus so we are sure to have a valid token before calling the API...
   */
  public componentDidMount() {
    if (this.activeScreenToDisplay && !this.sgConnectV2) {
      this.retrieveActiveScreen();
    }
  }

  private checkStatus(response: Response): Response {
    const [codeOK, codeKO] = [200, 300];
    if (response.status >= codeOK && response.status < codeKO) {
      return response;
    } else {
      throw new Error(response.statusText);
    }
  }

  private isEmpty(obj: object) {
    return !obj || Object.keys(obj).length === 0;
  }

  /**
   * Call the API of SGWT Services with SSO v1 Authentication - with SSO v1 cookie.
   */
  private callAPIWithSGConnectV1<T>(type: string, method: string, callback: (data: T) => void) {
    widgetConfiguration()
      .fetch(`${this.apiEndpoint}/splash-screens/${type}/${this.props.application}`, {
        headers: new Headers({
          accept: 'application/json'
        }),
        credentials: 'include',
        method
      })
      .then(this.checkStatus)
      .then(response => response.json())
      .then((data) => {
        callback(data);
      });
  }

  /**
   * Call the API of SGWT Services with SG Connect v2 Authentication - with Authorization Token.
   */
  private callAPIWithSGConnectV2<T>(type: string, method: string, callback: (data: T) => void) {
    if (this.authToken) {
      widgetConfiguration()
        .fetch(`${this.apiEndpoint}/splash-screens/${type}/${this.props.application}`, {
          headers:
            new Headers({
              Authorization: this.authToken
            }),
          method
        })
        .then(this.checkStatus)
        .then(response => response.json())
        .then((data) => {
          callback(data);
        });
    } else {
      widgetConfiguration().log('could not call Splash Screen API since we do not have SG Connect v2 token.');
    }
  }

  /**
   * Call the API. Check with authentication mode to know how to call it.
   */
  private callAPI<T>(type: string, method: string, callback: (data: T) => void) {
    if (this.sgConnectV2) {
      this.callAPIWithSGConnectV2(type, method, callback);
    } else {
      this.callAPIWithSGConnectV1(type, method, callback);
    }
  }

  /**
   * Retrieve an active screen for the current user and check the disclaimer status.
   */
  private retrieveActiveScreen() {
    this.logDebug('retrieve active screen');
    this.callAPI('active', 'GET', async (data: IActiveSplashScreen) => {
      this.logDebug('active screen found:', data);
      if (data.disclaimer && data.disclaimerApplication && this.apiDisclaimerEndpoint && this.disclaimerEndpoint) {
        // if disclaimer set to true and application Id set call the disclaimer api endpoint
        const application = data.disclaimerApplication;
        const disclaimerStatus = await this.checkDisclaimerStatus(application);
        // if disclaimer not accepted redirect to the disclaimer page and exit
        if (disclaimerStatus && disclaimerStatus.NeedsDisclaimerValidation) {
          const redirectUri = window.location.href;
          window.location.href = `${this.disclaimerEndpoint}/${application}?ReturnUrl=${encodeURIComponent(redirectUri)}`;
          return;
        }
      }
      this.retrieveScreen(data.screen);
    });
  }

  /**
   * Check the disclaimer status for the current user (SG Connect V2)
   */
  private async checkDisclaimerStatus(application: string): Promise<IServiceBoardDisclaimer | void> {
    if (this.authToken && application) {
      return await widgetConfiguration()
        .fetch(`${this.apiDisclaimerEndpoint}/${application}`, {
          headers: new Headers({
            accept: 'application/json',
            Authorization: this.authToken
          }),
          // credentials: 'include',
          method: 'GET'
        })
        .then(this.checkStatus)
        .then(response => response.json());
    }
  }

  /**
   * Retrieve an active screen for the current user. If it exists, it will be displayed and acknowledged.
   */
  private retrieveScreen(splashScreen?: ISplashScreen) {
    this.logDebug('retrieve screen');
    this.activeScreenToDisplay = false;
    if (!splashScreen || this.isEmpty(splashScreen)) {
      this.props.onNotShown();
      this.setState({
        show: false,
        screen: null
      });
    } else {
      this.props.onShow();
      this.setState({
        show: true,
        screen: splashScreen
      }, () => {
        this.callAPI('active', 'POST', () => {});
      });
    }
  }

  /**
   * Retrieve the history screens for the current user. If at least one screen exists, the history will be displayed.
   */
  private retrieveHistoryScreens() {
    this.logDebug('retrieve history screens');
    this.callAPI('history', 'GET', (data: ISplashScreen[]) => {
      this.logDebug('history screens found:', data);
      this.activeScreenToDisplay = false;
      this.setState({
        history: data,
        show: data.length > 0
      });
    });
  }

  /**
   * The modal has been hidden by the user.
   */
  hidingModal() {
    if (this.state.show) {
      this.props.onHide();
    }
    this.setState({
      show: false,
      screen: null
    });
  }

  /**
   * On Bootstrap, we can close the modal with the attribute `data-dismiss="modal"`.
   * It is not supported by react-bootstrap, so we fake it.
   * @param {Event} evt The click event.
   */
  panelClick(evt: Event) {
    const element = evt.target as HTMLElement;
    if (element.getAttribute('data-dismiss') === 'modal') {
      this.hidingModal();
    }
  }

  render(props: ISplashScreenProps, state: ISplashScreenState): JSX.Element {
    let body;
    if (props.mode === 'active') {
      const {screen} = state;
      if (!screen) {
        return <span/>;
      }
      // `<p dangerouslySetInnerHTML={{__html: '<strong>Hello</strong> world!'}}/>` does not work with
      // preact-compat + react-bootstrap.
      // Possible issue: https://github.com/developit/preact/issues/844
      // workaround: https://github.com/developit/preact/issues/844#issuecomment-325654148
      const InnerHTMLHelper = (props: { content: string }) => h('div', { dangerouslySetInnerHTML: { __html: props.content } });
      let logo = null;
      if (screen.logo) {
        logo = <img class="sgwt-splash-screen-application-logo" src={screen.logo} alt="Application logo"/>;
      }

      body =
        <div>
          <Modal.Header>
            <Modal.Title>
              {screen.title}
              {logo}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body bsClass="modal-body sgwt-splash-screen-body">
            <div onClick={(evt: Event) => { this.panelClick(evt); }}>
              <InnerHTMLHelper content={screen.content}/>
            </div>
          </Modal.Body>
          {
            screen.footer &&
              <Modal.Footer>
                <div onClick={(evt: Event) => { this.panelClick(evt); }}>
                  <InnerHTMLHelper content={screen.footer} />
                </div>
              </Modal.Footer>
          }
        </div>;
    } else {
      // In case we want to display the history but we do not have any screen to display, we return a span to avoid a
      // screen blink.
      if (state.history.length === 0) {
        return <span/>;
      }
      body =
        <SplashScreensCarousel
         screens={state.history}
        />;
    }

    return (
      <Modal
       show={state.show}
       onHide={() => { this.hidingModal(); } }
       bsSize="large"
      >
        {body}
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/sgwt-splash-screen/components/SplashScreen.tsx